import type { Level } from '@/domain/constants';
import type { StateChip } from '@/domain/state-chips';
import { lifecycleReading, type Glyph, type ResultReading } from '@/domain/tags';
import type { TimeValue } from '@/domain/time-value';
import { m, type Message } from '@/i18n';
import type { Actor } from '@/lib/directory';
import { dateAsStored } from './date-text';

/**
 * **What each value says in words** (docs/DESIGN_SYSTEM.md "Values and
 * entries": the accessible label is a sentence). Pure: one `Message` per data
 * type, the words a screen reader hears for what the eye reads off the value's
 * signs. Each component of the kit renders its own words from here, and a row
 * that shows values builds its sentence from the same functions, so the two
 * can never say one value two ways.
 *
 * The values themselves are typed by what they render (`ValueSpec`), never a
 * pre-formatted string where the type has structure.
 */

/** The units a duration is counted in (the Schedule's duration and offsets,
 *  the Resources estimate). */
export const DURATION_UNITS = ['hours', 'days', 'weeks', 'months'] as const;
export type DurationUnit = (typeof DURATION_UNITS)[number];

/** A health state as a value: its words (a platform key, or a flow's own word
 *  as `verbatim`) and its grade. */
export interface HealthState {
  label: Message;
  level: Level;
}

/** One value of one data type: what a `Value` renders and `valueWords` says. */
export type ValueSpec =
  | { type: 'person'; actor: Actor }
  | { type: 'group'; actor: Actor }
  | { type: 'item'; title: string; glyph: Glyph }
  | { type: 'hiddenItem'; cancelled?: boolean }
  | { type: 'stubItem'; title: string; result?: ResultReading; endsAfter?: boolean }
  | { type: 'date'; value: TimeValue | string; pastDue?: boolean }
  | { type: 'duration'; count: number; unit: DurationUnit }
  | { type: 'stage'; word: string }
  | { type: 'tag'; path: string }
  | { type: 'health'; state: HealthState }
  | { type: 'lifecycle'; chip: StateChip }
  | { type: 'number'; value: number }
  | { type: 'text'; text: string; markdown?: boolean }
  | { type: 'choice'; word: Message };

export type ValueType = ValueSpec['type'];
export type ValueOfType<T extends ValueType> = Extract<ValueSpec, { type: T }>;

/**
 * A change of one value: the old and the new of ONE type, or one value added
 * or removed. The mapped union refuses a change whose two sides differ in
 * type at compile time.
 */
export type ValueChange =
  | {
      [T in ValueType]: {
        from: ValueOfType<T>;
        to: ValueOfType<T>;
        added?: never;
        removed?: never;
      };
    }[ValueType]
  | { added: ValueSpec; from?: never; to?: never; removed?: never }
  | { removed: ValueSpec; from?: never; to?: never; added?: never };

/** How a date reads once the client knows the viewer's locale and clock
 *  (`dateText`); the clock-free token stands in until then. */
export type DateTextOf = (value: TimeValue | string) => string;

export function personWords(actor: Actor): Message {
  return m('values.person.words', { name: actor.name });
}

export function groupWords(actor: Actor): Message {
  return m('values.group.words', { name: actor.name });
}

/** An item's words: the title, and what its glyph says. A closed item is
 *  receded only while its health is clean (`closedQuiet`), so the two never
 *  speak together. */
export function itemWords({ title, glyph }: { title: string; glyph: Glyph }): Message {
  if (glyph.closed) {
    return lifecycleReading(glyph.standings) === 'cancelled'
      ? m('values.item.words.cancelled', { title })
      : m('values.item.words.done', { title });
  }
  if (glyph.health === 'critical') return m('values.item.words.critical', { title });
  if (glyph.health === 'warning') return m('values.item.words.warning', { title });
  return m('values.item.words.plain', { title });
}

/** An item the viewer sees only as its stub: its title, that the viewer has
 *  no access, its result where it is done or cancelled, and otherwise whether
 *  it now ends after this item's end. */
export function stubItemWords({
  title,
  result = 'open',
  endsAfter = false,
}: {
  title: string;
  result?: ResultReading;
  endsAfter?: boolean;
}): Message {
  if (result === 'done') return m('values.item.stubWords.done', { title });
  if (result === 'cancelled') return m('values.item.stubWords.cancelled', { title });
  if (endsAfter) return m('values.item.stubWords.endsAfter', { title });
  return m('values.item.stubWords.plain', { title });
}

export function hiddenItemWords({ cancelled = false }: { cancelled?: boolean }): Message {
  const hidden = m('values.item.hidden');
  return cancelled
    ? m('values.item.hiddenWords.cancelled', { hidden })
    : m('values.item.hiddenWords.plain', { hidden });
}

/** A date's words, from its text (`dateText`, or `dateAsStored` before mount). */
export function dateWords(date: string, pastDue = false): Message {
  return pastDue ? m('values.date.pastDue', { date }) : m('values.date.words', { date });
}

const UNIT_COUNT = {
  hours: 'item.sections.unitCount.hours',
  days: 'item.sections.unitCount.days',
  weeks: 'item.sections.unitCount.weeks',
  months: 'item.sections.unitCount.months',
} as const satisfies Record<DurationUnit, string>;

/** A duration's words, which are also its text: the count with its unit,
 *  plural by the catalog ("1 day", "0.5 days", "3 Tage"). */
export function durationWords({ count, unit }: { count: number; unit: DurationUnit }): Message {
  return m(UNIT_COUNT[unit], { count });
}

export function stageWords(word: string): Message {
  return m('values.stage.words', { stage: word });
}

export function tagWords(path: string): Message {
  return m('values.tag.words', { path });
}

const HEALTH_WORDS = {
  critical: 'values.health.words.critical',
  warning: 'values.health.words.warning',
  notice: 'values.health.words.notice',
  info: 'values.health.words.info',
} as const satisfies Record<Level, string>;

/** A health state's words: the state, and the grade its tone and dot say. */
export function healthWords(state: HealthState): Message {
  return m(HEALTH_WORDS[state.level], { state: state.label });
}

/** A lifecycle state's words: the chip's own word, the verdict's axis first. */
export function lifecycleWords(chip: StateChip): Message {
  return chip.axis
    ? m('values.lifecycle.withAxis', { axis: chip.axis, state: chip.label })
    : m('values.lifecycle.words', { state: chip.label });
}

/** A number's words, which are also its text (grouped from five digits on). */
export function numberWords(value: number): Message {
  return m('values.number.words', { value });
}

/** A choice (a field's option: a priority, a role, a unit, a flow field's
 *  option) is its word, which is also its text. */
export function choiceWords(word: Message): Message {
  return word;
}

/** Text a person typed, quoted in the locale's marks. */
export function quotedWords(text: string): Message {
  return m('values.quoted', { text });
}

/** A containment's words: who took it. Adopt is not a mark; it renders as
 *  the health pill it makes (docs/DESIGN_SYSTEM.md "The item page"). */
export function stanceWords(by: Actor): Message {
  return m('values.stance.contain', { name: by.name });
}

/** Any value's words. A date needs its text, which only the client can know
 *  (`dateText` after mount); the clock-free token stands in by default. */
export function valueWords(value: ValueSpec, dateTextOf: DateTextOf = dateAsStored): Message {
  switch (value.type) {
    case 'person':
      return personWords(value.actor);
    case 'group':
      return groupWords(value.actor);
    case 'item':
      return itemWords(value);
    case 'hiddenItem':
      return hiddenItemWords(value);
    case 'stubItem':
      return stubItemWords(value);
    case 'date':
      return dateWords(dateTextOf(value.value), value.pastDue);
    case 'duration':
      return durationWords(value);
    case 'stage':
      return stageWords(value.word);
    case 'tag':
      return tagWords(value.path);
    case 'health':
      return healthWords(value.state);
    case 'lifecycle':
      return lifecycleWords(value.chip);
    case 'number':
      return numberWords(value.value);
    case 'text':
      return quotedWords(value.text);
    case 'choice':
      return choiceWords(value.word);
  }
}

/** A change's words: "from X to Y", "added X", "removed X". */
export function changeWords(change: ValueChange, dateTextOf: DateTextOf = dateAsStored): Message {
  if (change.added)
    return m('values.change.added', { value: valueWords(change.added, dateTextOf) });
  if (change.removed) {
    return m('values.change.removed', { value: valueWords(change.removed, dateTextOf) });
  }
  return m('values.change.changed', {
    from: valueWords(change.from, dateTextOf),
    to: valueWords(change.to, dateTextOf),
  });
}
